/* @flow */
import idx from 'idx';
import React from 'react';
import { graphql } from 'react-apollo';
import PhoneCallPreferenceSection from 'settings/components/PhoneCallPreferenceSection';
import GET_PERMISSION_QUERY from '../queries/PhoneCallPreferencePermissionQuery.gql';
import UPDATE_PREFERENCE_MUTATION from '../queries/UpdatePhoneCallPreferenceMutation.gql';

import type { PhoneCallPreferencePermission } from '../queries/__generated__/PhoneCallPreferencePermission';
import type { updatePhoneCallPreferenceVariables } from '../queries/__generated__/updatePhoneCallPreference';

type Props = {
  smsSupported: boolean,
  mutate: (input: { variables: updatePhoneCallPreferenceVariables }) => Promise<*>,
  data: ReactApolloData<PhoneCallPreferencePermission>,
};

type State = {
  persisted: boolean,
  persistError: ?Error,
};

function buildOptimisticMutationResponse(props: Props, value: string) {
  const uuid = idx(props.data, _ => _.me.uuid);

  if (!uuid) {
    return undefined;
  }

  return {
    __typename: 'Mutation',
    updateUserPrefs: {
      __typename: 'UpdateUserPrefsPayload',
      user: {
        __typename: 'User',
        uuid,
        preferences: {
          __typename: 'UserPreferences',
          receive_phone_calls: value,
        },
      },
    },
  };
}

class PhoneCallPreferenceSectionContainer extends React.Component<Props, State> {
  state: State = {
    persisted: true,
    persistError: null,
  };

  listen = (mutator: () => Promise<*>): void => {
    this.setState({ persisted: false, persistError: null });

    mutator()
      .then(() => this.setState({ persisted: true }))
      .catch(e => this.setState({ persistError: e }));
  };

  enable = (): void => {
    this.listen(() =>
      this.props.mutate({
        variables: { value: 'enabled' },
        optimisticResponse: buildOptimisticMutationResponse(this.props, 'enabled'),
      })
    );
  };

  disable = (): void => {
    this.listen(() =>
      this.props.mutate({
        variables: { value: 'disabled' },
        optimisticResponse: buildOptimisticMutationResponse(this.props, 'disabled'),
      })
    );
  };

  isInitialDataMissing(): boolean {
    return !idx(this.props.data, _ => _.me);
  }

  hasInitialDataErrored(): boolean {
    return !!idx(this.props.data, _ => _.error);
  }

  isPermissionDenied(): boolean {
    return !idx(this.props.data, _ => _.me.permissions.can_edit_phone_call_preferences);
  }

  getPreferenceValue(): ?string {
    return idx(this.props.data, _ => _.me.preferences.receive_phone_calls);
  }

  isSmsSupported(): ?boolean {
    return idx(this.props.data, _ => _.me.sms_supported);
  }

  render() {
    if (this.isInitialDataMissing()) {
      return null;
    }

    if (this.hasInitialDataErrored()) {
      return null;
    }

    if (this.isPermissionDenied()) {
      return null;
    }

    if (!this.isSmsSupported()) {
      return null;
    }

    const value = this.getPreferenceValue();

    if (!value) {
      return null;
    }

    return (
      <PhoneCallPreferenceSection
        data-test="phone-call-preference-section"
        value={value}
        enable={this.enable}
        disable={this.disable}
        persisted={this.state.persisted}
        persistError={this.state.persistError}
        smsSupported={this.props.smsSupported}
      />
    );
  }
}

const withPhoneCallPreferenceMutation = graphql(UPDATE_PREFERENCE_MUTATION);
const withPermission = graphql(GET_PERMISSION_QUERY);

export { PhoneCallPreferenceSectionContainer };
export default withPermission(withPhoneCallPreferenceMutation(PhoneCallPreferenceSectionContainer));
